/**
 * @file     TypeTableBlock.tsx
 * @purpose  Renders a content-engine type_table block as a data table with right-aligned numerals and an inline share bar.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type TypeTableBlockProps = Extract<ContentBlock, { t: 'type_table' }>;

export const TypeTableBlock = ({ caption, rows }: TypeTableBlockProps) => {
  const { theme } = useAppTheme();
  const { t, i18n } = useTranslation();

  if (rows.length === 0) return null;

  const locale = i18n.language || 'en-CA';
  const formatCount = (n: number) => new Intl.NumberFormat(locale).format(n);
  const formatPrice = (n: number | null) =>
    n === null
      ? t('blog.blocks.typeTable.noData', { defaultValue: 'Not enough data' })
      // currencyDisplay narrowSymbol: the default renders CAD as "CA$", which
      // reads as a foreign currency to a BC audience.
      : new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: 'CAD',
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0,
        }).format(n);

  const headCell = {
    fontSize: theme.typography.caption,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    fontWeight: 600,
    color: theme.semanticColors.textMuted,
    p: '10px 12px',
    borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
    whiteSpace: 'nowrap' as const,
  };

  const cell = {
    fontSize: theme.typography.bodySm,
    color: theme.semanticColors.textSecondary,
    p: '12px',
    borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
    whiteSpace: 'nowrap' as const,
  };

  const numeric = { ...cell, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };

  return (
    <Box component="section">
      {caption ? (
        <Typography
          component="h2"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.bodyLg,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            mb: '16px',
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <Box sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 520, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" scope="col" sx={{ ...headCell, textAlign: 'left' }}>
                {t('blog.blocks.typeTable.type', { defaultValue: 'Property type' })}
              </Box>
              <Box component="th" scope="col" sx={{ ...headCell, textAlign: 'right' }}>
                {t('blog.blocks.typeTable.active', { defaultValue: 'Active' })}
              </Box>
              <Box component="th" scope="col" sx={{ ...headCell, textAlign: 'left', width: '30%' }}>
                {t('blog.blocks.typeTable.share', { defaultValue: 'Share' })}
              </Box>
              <Box component="th" scope="col" sx={{ ...headCell, textAlign: 'right' }}>
                {t('blog.blocks.typeTable.medianPrice', { defaultValue: 'Median price' })}
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {rows.map((row) => {
              const sharePct = Math.max(0, Math.min(1, row.share > 1 ? row.share / 100 : row.share));
              return (
                <Box
                  component="tr"
                  key={row.label}
                  sx={{
                    transition: 'background-color 180ms ease',
                    '&:hover': { backgroundColor: theme.semanticColors.surfaceElevated },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  <Box component="th" scope="row" sx={{ ...cell, textAlign: 'left', fontWeight: 500, color: theme.semanticColors.textPrimary }}>
                    {row.label}
                  </Box>
                  <Box component="td" sx={numeric}>
                    {formatCount(row.count)}
                  </Box>
                  <Box component="td" sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 6,
                          minWidth: 60,
                          borderRadius: `${theme.layout.radius.pill}px`,
                          backgroundColor: alpha(theme.rawSemanticColors.borderSubtle, 0.55),
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(sharePct * 100).toFixed(1)}%`,
                            borderRadius: `${theme.layout.radius.pill}px`,
                            backgroundColor: theme.semanticColors.interactivePrimary,
                          }}
                        />
                      </Box>
                      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: theme.semanticColors.textMuted }}>
                        {`${Math.round(sharePct * 100)}%`}
                      </Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={numeric}>
                    {formatPrice(row.medianPrice)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
